// Alta + pago "enterprise" desde una URL personalizada (una por plan, ej.
// `/alta-empresa/ultra-1`). Para clientes ya hablados por ventas: NO pasa por la
// generación de imagen de ejemplo del onboarding normal.
//
// Flujo (columna derecha, en pasos): crear cuenta / iniciar sesión → datos de la
// empresa (mismo form que el onboarding) → pago con Rebill → redirige a la app.
// La columna izquierda (resumen del plan) queda fija, igual que `Checkout.tsx`
// (mismo diseño; responsive: 2 columnas en desktop, stackeado en mobile).
// Ver ADR.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  Mail,
  Lock,
  ChevronDown,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';
import {
  getPlanById,
  ENTERPRISE_PLAN_SLUGS,
  paisFlags,
  paisDesdeCodigoISO,
  getPlanPriceForPais,
  formatPriceForPais,
} from '../lib/checkoutPlans';
import { CountrySelect, RubroSelect } from '../components/OnboardingSelects';
import { createCompanyForUser } from '../lib/createCompany';
import { isPaidPlan } from '../lib/planCapabilities';
import RebillCardPayment, {
  type RebillCheckoutResult,
} from '../components/RebillCardPayment';

type Step = 'loading' | 'auth' | 'datos' | 'pago' | 'exito';

// A dónde va tras pagar (el usuario pidió InterfazV2, sin imagen de ejemplo).
const POST_PAY_DEST = '/interfaz-v2';

// Decide el paso inicial según el estado de la cuenta. `'redirect'` = la cuenta
// YA tiene un plan pago → no mostramos el pago (evita re-cobro si vuelve al link)
// y la mandamos a la app.
async function resolveStepForUser(
  userId: string,
): Promise<'datos' | 'pago' | 'redirect'> {
  const { data: profile } = await supabase
    .from('profiles')
    .select('id_company')
    .eq('id', userId)
    .maybeSingle();
  const companyId = (profile as { id_company?: string | null } | null)?.id_company;
  if (!companyId) return 'datos';
  const { data: company } = await supabase
    .from('company_profiles')
    .select('plan')
    .eq('id_company', companyId)
    .maybeSingle();
  if (isPaidPlan((company as { plan?: string | null } | null)?.plan)) return 'redirect';
  return 'pago';
}

export const EnterpriseCheckout = () => {
  const navigate = useNavigate();
  const { plan: planSlug } = useParams<{ plan: string }>();

  const planId = planSlug ? ENTERPRISE_PLAN_SLUGS[planSlug] : undefined;
  const plan = planId ? getPlanById(planId) : undefined;

  const [step, setStep] = useState<Step>('loading');
  const [error, setError] = useState<string | null>(null);
  // "Ver qué incluye" (resumen compacto colapsable del plan, en mobile).
  const [showFeatures, setShowFeatures] = useState(false);
  // Cupón "Hot Sale" (el MISMO que el checkout): descuento del primer mes que se
  // auto-aplica y se pasa a Rebill, para que el precio de /alta-empresa matchee
  // el de la landing (evita el mismatch "el primer mes" vs precio full).
  const [coupon, setCoupon] = useState<{
    codigo: string;
    multiplicador: number;
  } | null>(null);

  // ── Auth ──
  const [authMode, setAuthMode] = useState<'signup' | 'signin'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // ── Datos de la empresa ──
  const [name, setName] = useState('');
  // País: se inicializa con lo que manda la landing en la URL
  // (`?pais=CL`) para poder mostrar el precio en la moneda correcta desde el
  // PRIMER render y dejar el select ya elegido. Si no viene o es un código
  // desconocido, queda vacío: el usuario lo elige y el precio se muestra en
  // USD hasta entonces, que es el fallback correcto. Ver ADR-592.
  const [pais, setPais] = useState(
    () =>
      paisDesdeCodigoISO(
        new URLSearchParams(window.location.search).get('pais'),
      ) ?? '',
  );
  const [companyName, setCompanyName] = useState('');
  const [industryOption, setIndustryOption] = useState('Moda e Indumentaria');
  const [customIndustry, setCustomIndustry] = useState('');
  const [datosLoading, setDatosLoading] = useState(false);

  // Al montar: si ya hay sesión, arrancar en el paso que corresponda.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!user) {
        setStep('auth');
        return;
      }
      const next = await resolveStepForUser(user.id);
      if (cancelled) return;
      if (next === 'redirect') {
        navigate(POST_PAY_DEST, { replace: true });
        return;
      }
      setStep(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // Tracking del embudo (app-side, best-effort) en `alta_empresa_clicks`. 3 eventos:
  //  · 'click'      → llegada al link (todos, antes de elegir signup/signin).
  //  · 'signup'     → el usuario SE REGISTRA (solo signup, NO signin).
  //  · 'conversion' → llega al paso de pago viniendo del signup (embudo del
  //                   cliente nuevo; el signin no cuenta, ya era cliente).
  // `source` = ?src (pricing/directo). Ver ADR/doc.
  const trackEvent = useCallback(
    (event: 'click' | 'signup' | 'conversion') => {
      if (!planSlug) return;
      const source =
        new URLSearchParams(window.location.search).get('src') || 'directo';
      void (async () => {
        try {
          await supabase
            .from('alta_empresa_clicks')
            .insert({ plan: planSlug, source, event });
        } catch {
          /* best-effort, no bloquea el alta */
        }
      })();
    },
    [planSlug],
  );

  // Guards con `ref` para no duplicar (StrictMode invoca effects 2× en dev;
  // también cubre re-montajes / re-submits).
  const clickTrackedRef = useRef(false);
  const didSignupRef = useRef(false); // vino por signup → gatea la conversión
  const conversionTrackedRef = useRef(false);

  useEffect(() => {
    if (!planId || !planSlug || clickTrackedRef.current) return;
    clickTrackedRef.current = true;
    trackEvent('click');
  }, [planId, planSlug, trackEvent]);

  // Auto-aplicar el cupón "Hot Sale" (descuento del primer mes), igual que el
  // checkout: mismo cupón por ID; si está activo/vigente y no agotado, se aplica
  // y se pasa a Rebill. Así el precio matchea la landing.
  useEffect(() => {
    // Descuento opcional: si la URL trae `?desc=0`, NO se aplica el cupón →
    // precio real. Sirve para que ventas mande un link a precio full cuando
    // quiera (ej. `alta-empresa/ultra-1?desc=0`). Sin el param → con descuento.
    if (new URLSearchParams(window.location.search).get('desc') === '0') return;
    let cancelled = false;
    (async () => {
      try {
        // RPC security-definer: valida vigencia server-side y devuelve SOLO el
        // cupón del alta (no expone la tabla `cupones` al usuario anónimo, que es
        // quien está en el paso de crear cuenta). Ver SQL en el doc/ADR.
        const { data } = await supabase.rpc('get_alta_empresa_coupon');
        const row = Array.isArray(data) ? data[0] : data;
        if (cancelled || !row) return;
        setCoupon({
          codigo: row.codigo,
          multiplicador: Number(row.multiplicador),
        });
      } catch {
        /* sin cupón → precio full */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setAuthLoading(true);
    try {
      if (authMode === 'signup') {
        const { data, error: signUpErr } = await supabase.auth.signUp({
          email: email.trim(),
          password,
        });
        if (signUpErr) throw signUpErr;
        if (!data.session) {
          // La cuenta ATLAS no pide confirmar mail, pero por las dudas.
          throw new Error('Revisá tu correo para confirmar la cuenta y volvé a entrar.');
        }
        setStep('datos');
        // Registro exitoso (solo signup, NO signin) → evento 'signup' del embudo.
        didSignupRef.current = true;
        trackEvent('signup');
      } else {
        const { data: signInData, error: signInErr } =
          await supabase.auth.signInWithPassword({
            email: email.trim(),
            password,
          });
        if (signInErr) throw signInErr;
        if (!signInData.user) {
          throw new Error('No se pudo obtener la sesión. Reintentá.');
        }
        const next = await resolveStepForUser(signInData.user.id);
        if (next === 'redirect') {
          navigate(POST_PAY_DEST, { replace: true });
          return;
        }
        setStep(next);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo continuar. Reintentá.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleDatos = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const industry =
      industryOption === 'Otro' ? customIndustry.trim() : industryOption;
    if (!name.trim()) return setError('El nombre es obligatorio');
    if (!pais) return setError('Seleccioná un país');
    if (!companyName.trim()) return setError('El nombre de la marca es obligatorio');
    if (!industry) return setError('El rubro es obligatorio');
    setDatosLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Sesión no encontrada. Volvé a iniciar sesión.');
      const res = await createCompanyForUser({
        userId: user.id,
        name,
        pais,
        companyName,
        industry,
      });
      if (!res.ok) throw new Error(res.error || 'No se pudo crear la empresa.');
      setStep('pago');
      // Llegó al pago viniendo del signup → 'conversion' del embudo. El signin no
      // cuenta (didSignupRef sólo se marca en signup).
      if (didSignupRef.current && !conversionTrackedRef.current) {
        conversionTrackedRef.current = true;
        trackEvent('conversion');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear la cuenta.');
    } finally {
      setDatosLoading(false);
    }
  };

  // ── Plan inválido (slug malo) ──
  if (!plan || !planId) {
    return (
      <div className="min-h-screen bg-surface-bg flex items-center justify-center p-6 text-center">
        <div className="max-w-md">
          <h1 className="text-[22px] font-semibold text-brand-black/90 mb-2">
            Link no válido
          </h1>
          <p className="text-[15px] text-neutral-500">
            Este link de alta no corresponde a un plan disponible. Pedile a tu
            contacto en Atlas el link correcto.
          </p>
        </div>
      </div>
    );
  }

  // `plan.price` es el precio en ARS; para el resto de los países se resuelve
  // con la misma tabla que usa el checkout normal (módulo compartido).
  const price = getPlanPriceForPais(plan.id, plan.price, pais);
  const esArgentina = paisFlags(pais).isArgentina;
  // Guard `> 0` además de `< 1`: si la DB tuviera `multiplicador = 0` (dato malo),
  // sin el guard `Math.floor(price*0)` mostraría "$0"; con el guard cae a precio
  // full. `Math.round` en vez de `floor` para redondear parejo.
  // Planes que NO admiten el cupón "Hot Sale". El Básico quedó afuera por
  // decisión comercial: su precio ya es el de entrada y no lleva promoción.
  //
  // Esto YA estaba bien en `Checkout.tsx`, que exige
  // `id === 'negocio' || isEnterprisePlan(id)`. Acá faltaba el filtro, así que
  // los links directos de la landing (`/alta-empresa/basico`) aplicaban el 30%
  // a un plan que no lo tiene. Y no era solo visual: el `couponCode` viaja a
  // `RebillCardPayment` y el SDK lo auto-aplica, o sea que se cobraba de menos.
  //
  // Se expresa como lista negra y no como lista blanca a propósito: si mañana
  // se agrega un plan nuevo, lo razonable es que herede la promo, no que quede
  // silenciosamente afuera.
  const PLANES_SIN_CUPON = ['emprendimiento'];
  // Además del plan, el cupón exige ARGENTINA: el "Hot Sale" es una promo
  // local y la landing solo muestra precio tachado cuando el país es AR
  // (`originalPrice: countryCode === "AR" ? … : null`). Sin este guard, un
  // chileno vería "el primer mes" con un descuento que su cobro por Stripe no
  // aplica — o sea, exactamente el engaño que estamos sacando. Ver ADR-592.
  const planAdmiteCupon =
    !PLANES_SIN_CUPON.includes(plan.id) && paisFlags(pais).isArgentina;

  const mult =
    planAdmiteCupon &&
    coupon &&
    coupon.multiplicador > 0 &&
    coupon.multiplicador < 1
      ? coupon.multiplicador
      : null;
  const hasDiscount = mult !== null;
  const bigPrice = mult !== null ? Math.round(price * mult) : price;
  const priceLabel = formatPriceForPais(bigPrice, pais); // grande (con descuento si hay)
  const fullPriceLabel = formatPriceForPais(price, pais); // full ("Después … / mes")

  // ── Pago del exterior: Stripe por redirect ───────────────────────────
  // Argentina cobra con el checkout embebido de Rebill (in-app). El resto del
  // mundo va a Stripe, que es un REDIRECT: se le pide la URL al webhook de n8n
  // y se manda al usuario para afuera. Es el mismo endpoint y el mismo shape
  // que usa `Checkout.tsx` para los no-argentinos; acá no se reinventa nada.
  //
  // OJO con la consecuencia de UX: al ser redirect, el usuario SE VA de esta
  // página, así que la pantalla de éxito de este flujo (`step === 'exito'`) no
  // se muestra para el exterior. Vuelve adonde n8n tenga configurado el
  // `success_url` de Stripe.
  const [stripeLoading, setStripeLoading] = useState(false);
  const irAStripe = async () => {
    if (stripeLoading) return;
    setStripeLoading(true);
    setError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Sesión no encontrada. Volvé a iniciar sesión.');

      const res = await fetch(
        'https://ai-agency-dt7d.onrender.com/webhook/stripe-start',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: GENERATE_AUTH_TOKEN,
          },
          body: JSON.stringify({
            user_id: user.id,
            plan: planId,
            // SIN cupón, por dos razones. Primero: el Hot Sale es una promo
            // argentina y este camino es justamente el del exterior, así que
            // no corresponde. Segundo, aunque correspondiera, no se podría
            // mandar bien: el RPC `get_alta_empresa_coupon` devuelve solo
            // `(codigo, multiplicador)` y el webhook espera `cupon_id`.
            // Mandar `con_descuento: true` sin el id sería un payload a medio
            // armar contra un endpoint que COBRA. Antes de eso, precio full.
          }),
        },
      );
      if (!res.ok) throw new Error('No se pudo iniciar el pago.');
      const data = await res.json();
      if (!data?.url) throw new Error('No se recibió una URL de pago válida.');
      window.location.href = data.url;
    } catch (err: any) {
      setError(err?.message || 'No se pudo iniciar el pago.');
      setStripeLoading(false);
    }
  };

  // ── Resumen del plan (columna izquierda / cabecera mobile) ──
  // OJO: son funciones que se LLAMAN ({renderX()}), no componentes (<X />).
  // Definirlos como componentes acá adentro haría que React los re-monte en
  // cada render (nueva identidad de función) → el input pierde el foco al tipear.
  const renderPlanSummary = () => (
    <>
      <div className="max-w-[440px]">
        <p className="text-[15px] text-neutral-500 mb-1">
          Suscribite al Plan {plan.name}
        </p>
        <div className="flex items-baseline gap-2.5">
          <span className="text-[46px] leading-none font-bold text-brand-black/90 tracking-tight">
            {priceLabel}
          </span>
          <span className="text-[14px] text-neutral-500">
            {hasDiscount ? 'el primer mes' : 'por mes'}
          </span>
        </div>
        {hasDiscount && (
          <p className="text-[13px] text-neutral-600 mt-2">
            Después {fullPriceLabel} / mes
          </p>
        )}
        <ul className="mt-10 space-y-3">
          {plan.features.map((feature, i) => (
            <li
              key={i}
              className="flex items-center text-[14px] text-neutral-600"
            >
              <CheckCircle2 className="w-4 h-4 text-brand-primary mr-2.5 flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-auto pt-10 text-[12px] text-neutral-400">
        Se renueva automáticamente cada mes. Cancelás cuando quieras.
      </p>
    </>
  );

  // ── Contenido del paso actual (columna derecha) ──
  const renderStepContent = () => {
    if (step === 'loading') {
      return (
        <div className="flex items-center justify-center gap-2 py-10 text-[15px] text-neutral-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      );
    }

    if (step === 'exito') {
      return (
        <div>
          <h2 className="text-[24px] font-bold text-brand-black/90 tracking-tight mb-2">
            ¡Listo! Tu plan está activo
          </h2>
          <p className="text-[15px] text-neutral-500 mb-8">
            Ya podés empezar a usar Atlas con todo tu plan {plan.name}.
          </p>
          <button
            type="button"
            onClick={() => navigate(POST_PAY_DEST, { replace: true })}
            className="inline-flex items-center justify-center py-3 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
          >
            Empezar a usar Atlas
          </button>
        </div>
      );
    }

    if (step === 'pago') {
      // Argentina → Rebill embebido (in-app). Resto → Stripe (redirect).
      // Misma regla que `Checkout.tsx`; antes esta pantalla montaba Rebill para
      // todo el mundo y le mostraba precios en ARS a un chileno. Ver ADR-592.
      if (!esArgentina) {
        return (
          <div className="space-y-4">
            <p className="text-[14.5px] text-[#3C3C43]/70 leading-snug">
              Te vamos a llevar a Stripe para completar tu suscripción de forma
              segura. Volvés a Atlas apenas termines.
            </p>
            <button
              type="button"
              onClick={irAStripe}
              disabled={stripeLoading}
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white text-[15px] font-semibold shadow-sm transition-colors"
            >
              {stripeLoading ? 'Redirigiendo…' : 'Continuar al pago'}
            </button>
          </div>
        );
      }
      // Sin título extra: va directo al form de Rebill, igual que el checkout.
      return (
        <RebillCardPayment
          planId={planId}
          couponCode={hasDiscount ? (coupon?.codigo ?? null) : null}
          onSuccess={(_r: RebillCheckoutResult) => setStep('exito')}
          onError={(msg) => setError(msg)}
        />
      );
    }

    if (step === 'datos') {
      return (
        <form onSubmit={handleDatos} className="space-y-6">
          <div>
            <h2 className="text-[22px] font-semibold text-brand-black/90 mb-1">
              Contanos de tu marca
            </h2>
            <p className="text-[14px] text-neutral-500">
              Con estos datos armamos tu cuenta.
            </p>
          </div>
          <div>
            <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
              Nombre y apellido
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
              placeholder="Tu nombre"
              required
            />
          </div>
          <div>
            <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
              País
            </label>
            <CountrySelect value={pais} onChange={setPais} required />
          </div>
          <div>
            <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
              Nombre de la marca
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
              placeholder="Nombre de tu marca o negocio"
              required
            />
          </div>
          <div>
            <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
              Rubro
            </label>
            <div className="flex flex-col md:flex-row gap-3">
              <div className={industryOption === 'Otro' ? 'md:w-1/2' : 'w-full'}>
                <RubroSelect value={industryOption} onChange={setIndustryOption} />
              </div>
              {industryOption === 'Otro' && (
                <input
                  type="text"
                  value={customIndustry}
                  onChange={(e) => setCustomIndustry(e.target.value)}
                  placeholder="Especificá tu rubro"
                  className="w-full md:w-1/2 px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
                  required
                />
              )}
            </div>
          </div>
          <button
            type="submit"
            disabled={datosLoading}
            className="w-full h-[var(--btn-h)] px-8 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[15px] shadow-sm transition-colors disabled:opacity-50"
          >
            {datosLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Creando tu cuenta…
              </>
            ) : (
              'Continuar'
            )}
          </button>
        </form>
      );
    }

    // step === 'auth'
    return (
      <form onSubmit={handleAuth} className="space-y-5">
        <div>
          <h2 className="text-[22px] font-semibold text-brand-black/90 mb-1">
            {authMode === 'signup' ? 'Creá tu cuenta' : 'Iniciá sesión'}
          </h2>
          <p className="text-[14px] text-neutral-500">
            {authMode === 'signup'
              ? 'Empezá tu suscripción al plan ' + plan.name + '.'
              : 'Entrá con tu cuenta para continuar.'}
          </p>
        </div>
        <div>
          <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
            Email
          </label>
          <div className="relative">
            <Mail className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
              placeholder="tu@email.com"
              required
            />
          </div>
        </div>
        <div>
          <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
            Contraseña
          </label>
          <div className="relative">
            <Lock className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
              placeholder="••••••••"
              minLength={6}
              required
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={authLoading}
          className="w-full h-[var(--btn-h)] px-8 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[15px] shadow-sm transition-colors disabled:opacity-50"
        >
          {authLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Un momento…
            </>
          ) : authMode === 'signup' ? (
            'Crear cuenta'
          ) : (
            'Iniciar sesión'
          )}
        </button>
        <p className="text-[13px] text-neutral-500 text-center">
          {authMode === 'signup' ? '¿Ya tenés cuenta? ' : '¿No tenés cuenta? '}
          <button
            type="button"
            onClick={() => {
              setAuthMode(authMode === 'signup' ? 'signin' : 'signup');
              setError(null);
            }}
            className="text-brand-primary font-medium hover:underline"
          >
            {authMode === 'signup' ? 'Iniciá sesión' : 'Creá una'}
          </button>
        </p>
      </form>
    );
  };

  const renderLogo = (size: 'sm' | 'md' = 'md') => (
    <div className="flex items-center gap-2.5">
      <div
        className={`${size === 'sm' ? 'w-7 h-7' : 'w-9 h-9'} rounded-full bg-brand-black flex items-center justify-center shrink-0`}
      >
        <img
          src="/images/estudio-atlas-logo-solo-blanco.svg"
          alt=""
          className={size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'}
        />
      </div>
      <span
        className={`${size === 'sm' ? 'text-[14px]' : 'text-[16px]'} font-semibold text-brand-black/90`}
      >
        Estudio Atlas
      </span>
    </div>
  );

  const renderErrorBanner = () =>
    error ? (
      <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>{error}</span>
      </div>
    ) : null;

  return (
    <div
      // En desktop la página es su propio contenedor de scroll con el gutter de
      // la scrollbar RESERVADO (scrollbar-gutter: stable): así, abrir el dropdown
      // de rubro (que se pasa del viewport) NO hace aparecer/desaparecer la barra
      // del body ni corre el layout a la izquierda. Fondo blanco en desktop → el
      // gutter reservado (extremo derecho) queda invisible.
      className="min-h-screen md:h-screen md:overflow-y-auto bg-surface-card"
      style={{ scrollbarGutter: 'stable' }}
    >
      {/* ═══════════ MOBILE (homogéneo con /checkout) ═══════════ */}
      <div className="md:hidden flex flex-col min-h-screen pb-10">
        {/* Header: logo + "Pago seguro" (igual que el checkout). */}
        <div
          className="px-5 pb-2 flex items-center justify-between"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.25rem)' }}
        >
          {renderLogo('sm')}
          <div className="flex items-center gap-1 text-[11px] text-neutral-400">
            <Lock className="w-3 h-3" />
            <span>Pago seguro</span>
          </div>
        </div>

        {/* Resumen del plan compacto y centrado + "Ver qué incluye" colapsable
            (features ocultas por defecto), igual que el checkout. */}
        <div className="px-5 pt-7 pb-5 border-b border-neutral-200 text-center">
          <p className="text-[13px] text-neutral-500">Plan {plan.name}</p>
          <p className="text-[28px] font-bold text-brand-black/90 tracking-tight leading-tight mt-1">
            {priceLabel}
            <span className="text-[13px] font-normal text-neutral-400 ml-1">
              {hasDiscount ? 'el primer mes' : '/ mes'}
            </span>
          </p>
          {hasDiscount && (
            <p className="text-[13px] text-neutral-600 mt-0.5">
              Después {fullPriceLabel} por mes
            </p>
          )}
          <button
            type="button"
            onClick={() => setShowFeatures(!showFeatures)}
            className="mt-3 inline-flex items-center gap-1 text-[13px] text-brand-primary hover:text-brand-primary-hover font-medium transition-colors"
          >
            Ver qué incluye
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${showFeatures ? 'rotate-180' : ''}`}
            />
          </button>
          {showFeatures && (
            <ul className="mt-3 space-y-2 text-left">
              {plan.features.map((feature, i) => (
                <li
                  key={i}
                  className="flex items-center text-[13px] text-neutral-600"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 text-brand-primary mr-2 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Contenido del paso (auth / datos / pago). */}
        <div className="px-5 pt-5">
          {renderErrorBanner()}
          {renderStepContent()}
        </div>
      </div>

      {/* ═══════════ DESKTOP ═══════════ */}
      <div className="hidden md:flex min-h-screen">
        {/* Izquierda — resumen del plan */}
        <div className="w-1/2 bg-[#DDDFE6] flex flex-col px-10 lg:px-20 py-12">
          <div className="mb-16">
            {renderLogo()}
          </div>
          {renderPlanSummary()}
        </div>
        {/* Derecha — el paso actual */}
        <div className="w-1/2 bg-surface-card flex flex-col justify-center px-10 lg:px-20 py-12">
          <div className="w-full mx-auto max-w-[480px]">
            {renderErrorBanner()}
            {renderStepContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseCheckout;
